import {
  AANAccountType,
  AccountTypes,
  IndividualDesignationListedTypes,
  IRADesignationListedTypes,
  IRATypes,
  ISelectedAccount,
  YesNoEmpty,
} from '@interfaces/models';

import { TriageAccountType, TriageAccountTypeNames } from '../models';
import {
  TriageRelationshipType,
  TriageRelationshipTypeNames,
} from '../models/triage-relationship.model';

export function getFormAccountType(
  accountTypeInheriting: AccountTypes,
  iraType: IRATypes,
  inheritanceIRAQuestionToggle: boolean,
): TriageAccountType {
  switch (accountTypeInheriting) {
    case AccountTypes.INDV:
      return TriageAccountType.INDV;
    case AccountTypes.JOINT:
    case AccountTypes.JOINT_COMMUNITY_PROPERTY:
    case AccountTypes.JOINT_TENANTS_WROS:
      return TriageAccountType.JOINT;
    case AccountTypes.IRA: {
      switch (iraType) {
        case IRATypes.OTHER:
          return TriageAccountType.TRADITIONAL_IRA;
        case IRATypes.ROTH:
          return TriageAccountType.ROTH_IRA;
        case IRATypes.INHERITED_OTHER:
          return inheritanceIRAQuestionToggle
            ? TriageAccountType.TRADITIONAL_IRA
            : TriageAccountType.INHERITED_TRADITIONAL_IRA;
        case IRATypes.INHERITED_ROTH:
          return inheritanceIRAQuestionToggle
            ? TriageAccountType.ROTH_IRA
            : TriageAccountType.INHERITED_ROTH_IRA;
        default:
          return TriageAccountType.EMPTY;
      }
    }
    default:
      return TriageAccountType.EMPTY;
  }
}

export function getAccountTypeFromTriage(
  triageAccountType: TriageAccountType,
  isAccountInherited: YesNoEmpty,
): {
  accountType: AccountTypes;
  iraType?: IRATypes;
} {
  switch (triageAccountType) {
    case TriageAccountType.INDV:
      return { accountType: AccountTypes.INDV };
    case TriageAccountType.JOINT:
      return { accountType: AccountTypes.JOINT };
    case TriageAccountType.TRADITIONAL_IRA:
      return {
        accountType: AccountTypes.IRA,
        iraType: isAccountInherited === YesNoEmpty.YES ? IRATypes.INHERITED_OTHER : IRATypes.OTHER,
      };
    case TriageAccountType.ROTH_IRA:
      return {
        accountType: AccountTypes.IRA,
        iraType: isAccountInherited === YesNoEmpty.YES ? IRATypes.INHERITED_ROTH : IRATypes.ROTH,
      };
    case TriageAccountType.INHERITED_TRADITIONAL_IRA:
      return { accountType: AccountTypes.IRA, iraType: IRATypes.INHERITED_OTHER };
    case TriageAccountType.INHERITED_ROTH_IRA:
      return { accountType: AccountTypes.IRA, iraType: IRATypes.INHERITED_ROTH };
    default:
      return { accountType: AccountTypes.EMPTY };
  }
}

export function getFormRelationshipType(
  triageAccountType: AccountTypes,
  isSpouse: string,
  IRADesignation: IRADesignationListedTypes,
  individualDesignation: IndividualDesignationListedTypes,
): TriageRelationshipType {
  switch (isSpouse) {
    case 'YES':
      return TriageRelationshipType.SPOUSE;
    case 'NO':
    case '':
      switch (triageAccountType) {
        case AccountTypes.IRA:
          // Check individualDesignation first for EXECUTOR (IRA + Estate toggle scenario)
          if (individualDesignation === IndividualDesignationListedTypes.EXECUTOR) {
            return TriageRelationshipType.EXECUTOR;
          }

          switch (IRADesignation) {
            case IRADesignationListedTypes.NAME:
              return TriageRelationshipType.BENEFICIARY;
            case IRADesignationListedTypes.RELATIONSHIP:
              return TriageRelationshipType.PERSTIRPES;
            case IRADesignationListedTypes.EXECUTOR:
              return TriageRelationshipType.EXECUTOR;
            case IRADesignationListedTypes.TRUSTEE:
              return TriageRelationshipType.TRUSTEE;
            default:
              return TriageRelationshipType.EMPTY;
          }
        case AccountTypes.INDV:
          switch (individualDesignation) {
            case IndividualDesignationListedTypes.EXECUTOR:
              return TriageRelationshipType.EXECUTOR;
            case IndividualDesignationListedTypes.BENEFICIARY:
              return TriageRelationshipType.BENEFICIARY;
            case IndividualDesignationListedTypes.TRUSTEE:
              return TriageRelationshipType.TRUSTEE;
            case IndividualDesignationListedTypes.EMPTY:
            default:
              return TriageRelationshipType.EMPTY;
          }
        case AccountTypes.JOINT:
          switch (individualDesignation) {
            case IndividualDesignationListedTypes.TRUSTEE:
              return TriageRelationshipType.TRUSTEE;
            case IndividualDesignationListedTypes.BENEFICIARY:
              return TriageRelationshipType.BENEFICIARY;
            case IndividualDesignationListedTypes.EMPTY:
            default:
              return TriageRelationshipType.EMPTY;
          }
      }
    default:
      return TriageRelationshipType.EMPTY;
  }
}

export function getRelationshipTypeFromTriage(
  triageRelationshipType: TriageRelationshipType | undefined,
  accountType: AccountTypes,
): {
  isSpouse: string;
  IRADesignation: IRADesignationListedTypes;
  individualDesignation: IndividualDesignationListedTypes;
} {
  const defaultResult = {
    isSpouse: '',
    IRADesignation: IRADesignationListedTypes.EMPTY,
    individualDesignation: IndividualDesignationListedTypes.EMPTY,
  };
  if (!triageRelationshipType) {
    return defaultResult;
  }
  switch (accountType) {
    case AccountTypes.INDV:
      switch (triageRelationshipType) {
        case TriageRelationshipType.SPOUSE:
          return {
            isSpouse: 'YES',
            IRADesignation: IRADesignationListedTypes.EMPTY,
            individualDesignation: IndividualDesignationListedTypes.BENEFICIARY,
          };
        case TriageRelationshipType.BENEFICIARY:
          return {
            isSpouse: 'NO',
            IRADesignation: IRADesignationListedTypes.NAME,
            individualDesignation: IndividualDesignationListedTypes.BENEFICIARY,
          };
        case TriageRelationshipType.EXECUTOR:
          return {
            isSpouse: 'NO',
            IRADesignation: IRADesignationListedTypes.EMPTY,
            individualDesignation: IndividualDesignationListedTypes.EXECUTOR,
          };
        case TriageRelationshipType.TRUSTEE:
          return {
            isSpouse: 'NO',
            IRADesignation: IRADesignationListedTypes.EMPTY,
            individualDesignation: IndividualDesignationListedTypes.TRUSTEE,
          };
        default:
          return defaultResult;
      }
    case AccountTypes.JOINT:
      switch (triageRelationshipType) {
        case TriageRelationshipType.SPOUSE:
          return {
            isSpouse: 'YES',
            IRADesignation: IRADesignationListedTypes.EMPTY,
            individualDesignation: IndividualDesignationListedTypes.EMPTY,
          };
        case TriageRelationshipType.BENEFICIARY:
          return {
            isSpouse: 'NO',
            IRADesignation: IRADesignationListedTypes.EMPTY,
            individualDesignation: IndividualDesignationListedTypes.BENEFICIARY,
          };
        case TriageRelationshipType.TRUSTEE:
          return {
            isSpouse: 'NO',
            IRADesignation: IRADesignationListedTypes.EMPTY,
            individualDesignation: IndividualDesignationListedTypes.TRUSTEE,
          };
        default:
          return defaultResult;
      }
    case AccountTypes.IRA:
      switch (triageRelationshipType) {
        case TriageRelationshipType.SPOUSE:
          return {
            isSpouse: 'YES',
            IRADesignation: IRADesignationListedTypes.NAME,
            individualDesignation: IndividualDesignationListedTypes.EMPTY,
          };
        case TriageRelationshipType.BENEFICIARY:
          return {
            isSpouse: 'NO',
            IRADesignation: IRADesignationListedTypes.NAME,
            individualDesignation: IndividualDesignationListedTypes.EMPTY,
          };
        case TriageRelationshipType.PERSTIRPES:
          return {
            isSpouse: 'NO',
            IRADesignation: IRADesignationListedTypes.RELATIONSHIP,
            individualDesignation: IndividualDesignationListedTypes.EMPTY,
          };
        case TriageRelationshipType.EXECUTOR:
          return {
            isSpouse: 'NO',
            IRADesignation: IRADesignationListedTypes.EXECUTOR,
            individualDesignation: IndividualDesignationListedTypes.EMPTY,
          };
        case TriageRelationshipType.TRUSTEE:
          return {
            isSpouse: 'NO',
            IRADesignation: IRADesignationListedTypes.TRUSTEE,
            individualDesignation: IndividualDesignationListedTypes.EMPTY,
          };
        default:
          return defaultResult;
      }
    default:
      return defaultResult;
  }
}

export const triageAccountTypesMap = new Map<TriageAccountType, TriageAccountTypeNames>([
  [TriageAccountType.INDV, TriageAccountTypeNames.INDV],
  [TriageAccountType.JOINT, TriageAccountTypeNames.JOINT],
  [TriageAccountType.TRADITIONAL_IRA, TriageAccountTypeNames.TRADITIONAL_IRA],
  [TriageAccountType.ROTH_IRA, TriageAccountTypeNames.ROTH_IRA],
  [TriageAccountType.INHERITED_TRADITIONAL_IRA, TriageAccountTypeNames.INHERITED_TRADITIONAL_IRA],
  [TriageAccountType.INHERITED_ROTH_IRA, TriageAccountTypeNames.INHERITED_ROTH_IRA],
]);

export const isAccountTypeAllowedByToggle = (key: TriageAccountType, toggle: boolean) => {
  if (toggle) {
    return (
      key !== TriageAccountType.INHERITED_TRADITIONAL_IRA &&
      key !== TriageAccountType.INHERITED_ROTH_IRA
    );
  }
  return true;
};

export const aanToTriageAccountTypeMap: Partial<Record<AANAccountType, TriageAccountType[]>> = {
  [AANAccountType.INDIVIDUAL]: [TriageAccountType.INDV],
  [AANAccountType.JOINT]: [TriageAccountType.JOINT],
  [AANAccountType.JOINT_TENANTS_IN_COMMON]: [TriageAccountType.JOINT],
  [AANAccountType.JOINT_TENANTS_WROS]: [TriageAccountType.JOINT],
  [AANAccountType.SEP_IRA]: [TriageAccountType.TRADITIONAL_IRA],
  [AANAccountType.SEP_IRA_SINGLE_PARTICIPANT]: [TriageAccountType.TRADITIONAL_IRA],
  [AANAccountType.ROLLOVER_IRA]: [TriageAccountType.TRADITIONAL_IRA],
  [AANAccountType.TRADITIONAL_IRA]: [TriageAccountType.TRADITIONAL_IRA],
  [AANAccountType.ROTH_IRA]: [TriageAccountType.ROTH_IRA],
  [AANAccountType.CONVERSION_ROTH_IRA]: [TriageAccountType.ROTH_IRA],
  [AANAccountType.INHERITED_IRA]: [
    TriageAccountType.TRADITIONAL_IRA,
    TriageAccountType.INHERITED_TRADITIONAL_IRA,
  ],
  [AANAccountType.INHERITED_ROTH_IRA]: [
    TriageAccountType.ROTH_IRA,
    TriageAccountType.INHERITED_ROTH_IRA,
  ],
};

export const isAccountTypeAllowedByDecedent = (
  key: TriageAccountType,
  aanAccounts: Set<AANAccountType>,
) => {
  if (!aanAccounts.size) {
    return true;
  }

  const arr = Array.from(aanAccounts);
  const fullList = new Set(
    arr
      .map((aanAccount) => aanToTriageAccountTypeMap[aanAccount])
      .flat()
      .filter(Boolean),
  );
  if (!fullList.size) {
    return true;
  }
  return fullList.has(key);
};

export const hasUnhandledAANAccountTypes = (aanAccounts: Set<AANAccountType>): boolean => {
  if (!aanAccounts.size) return false;
  return Array.from(aanAccounts).some((type) => aanToTriageAccountTypeMap[type] === undefined);
};

const NON_INHERITED_TRAD_IRA_ACCOUNT_TYPES = new Set<AANAccountType>([
  AANAccountType.SEP_IRA,
  AANAccountType.SEP_IRA_SINGLE_PARTICIPANT,
  AANAccountType.ROLLOVER_IRA,
  AANAccountType.TRADITIONAL_IRA,
]);

const INHERITED_IRA_ACCOUNT_TYPES = new Set<AANAccountType>([
  AANAccountType.INHERITED_IRA,
  AANAccountType.INHERITED_ROTH_IRA,
]);

export const getImpliedInheritedAccountAnswer = (aanAccounts: Set<AANAccountType>): YesNoEmpty => {
  if (!aanAccounts.size) {
    return YesNoEmpty.EMPTY;
  }
  const hasNonInheritedTradIra = Array.from(aanAccounts).some((type) =>
    NON_INHERITED_TRAD_IRA_ACCOUNT_TYPES.has(type),
  );
  const hasInheritedIra = Array.from(aanAccounts).some((type) =>
    INHERITED_IRA_ACCOUNT_TYPES.has(type),
  );

  if (hasNonInheritedTradIra && hasInheritedIra) {
    return YesNoEmpty.EMPTY;
  }
  if (hasNonInheritedTradIra) {
    return YesNoEmpty.NO;
  }
  if (hasInheritedIra) {
    return YesNoEmpty.YES;
  }
  return YesNoEmpty.EMPTY;
};

export const shouldHideInheritedAccountQuestion = (aanAccounts: Set<AANAccountType>): boolean =>
  getImpliedInheritedAccountAnswer(aanAccounts) !== YesNoEmpty.EMPTY;

export const triageRelationshipTypesMap = new Map<
  TriageRelationshipType,
  TriageRelationshipTypeNames
>([
  [TriageRelationshipType.SPOUSE, TriageRelationshipTypeNames.SPOUSE],
  [TriageRelationshipType.BENEFICIARY, TriageRelationshipTypeNames.BENEFICIARY],
  [TriageRelationshipType.PERSTIRPES, TriageRelationshipTypeNames.PERSTIRPES],
  [TriageRelationshipType.EXECUTOR, TriageRelationshipTypeNames.EXECUTOR],
  [TriageRelationshipType.TRUSTEE, TriageRelationshipTypeNames.TRUSTEE],
]);

export function getSelectedJointAccounts(
  eligibleAccounts: ISelectedAccount[],
  selectedAccounts: ISelectedAccount[],
): boolean[] {
  const formJointAccounts: boolean[] = new Array(eligibleAccounts.length).fill(false);
  selectedAccounts.forEach((selectedAccount: ISelectedAccount) => {
    const selectedEligibleAccountIndex = eligibleAccounts.findIndex(
      (account: ISelectedAccount) =>
        account.accountData.accountId === selectedAccount.accountData.accountId,
    );
    if (selectedEligibleAccountIndex >= 0) {
      formJointAccounts[selectedEligibleAccountIndex] = true;
    }
  });

  return formJointAccounts;
}

/**
 * Configuration for allowed relationships per account type.
 */
export const ALLOWED_RELATIONSHIPS_CONFIG = {
  [TriageAccountType.INDV]: {
    default: new Set([TriageRelationshipType.SPOUSE, TriageRelationshipType.BENEFICIARY]),
    withEstateToggle: new Set([
      TriageRelationshipType.SPOUSE,
      TriageRelationshipType.BENEFICIARY,
      TriageRelationshipType.EXECUTOR,
    ]),
    withIndividualJointToTrustToggle: new Set([
      TriageRelationshipType.SPOUSE,
      TriageRelationshipType.BENEFICIARY,
      TriageRelationshipType.TRUSTEE,
    ]),
  },
  [TriageAccountType.JOINT]: {
    default: new Set([TriageRelationshipType.SPOUSE, TriageRelationshipType.BENEFICIARY]),
    withIndividualJointToTrustToggle: new Set([
      TriageRelationshipType.SPOUSE,
      TriageRelationshipType.BENEFICIARY,
      TriageRelationshipType.TRUSTEE,
    ]),
  },
  [TriageAccountType.TRADITIONAL_IRA]: {
    default: new Set([
      TriageRelationshipType.SPOUSE,
      TriageRelationshipType.BENEFICIARY,
      TriageRelationshipType.PERSTIRPES,
    ]),
    withEstateToggle: new Set([
      TriageRelationshipType.SPOUSE,
      TriageRelationshipType.BENEFICIARY,
      TriageRelationshipType.PERSTIRPES,
      TriageRelationshipType.TRUSTEE,
      TriageRelationshipType.EXECUTOR,
    ]),
  },
  [TriageAccountType.ROTH_IRA]: {
    default: new Set([
      TriageRelationshipType.SPOUSE,
      TriageRelationshipType.BENEFICIARY,
      TriageRelationshipType.PERSTIRPES,
    ]),
    withEstateToggle: new Set([
      TriageRelationshipType.SPOUSE,
      TriageRelationshipType.BENEFICIARY,
      TriageRelationshipType.PERSTIRPES,
      TriageRelationshipType.TRUSTEE,
      TriageRelationshipType.EXECUTOR,
    ]),
  },
  [TriageAccountType.INHERITED_TRADITIONAL_IRA]: {
    default: new Set([
      TriageRelationshipType.SPOUSE,
      TriageRelationshipType.BENEFICIARY,
      TriageRelationshipType.PERSTIRPES,
    ]),
  },
  [TriageAccountType.INHERITED_ROTH_IRA]: {
    default: new Set([
      TriageRelationshipType.SPOUSE,
      TriageRelationshipType.BENEFICIARY,
      TriageRelationshipType.PERSTIRPES,
    ]),
  },
  [TriageAccountType.EMPTY]: {
    default: new Set([]),
  },
} as const;

/**
 * Display order for relationship types (for UI presentation only).
 * This is separate from the allowed relationships logic.
 */
const RELATIONSHIP_DISPLAY_ORDER = [
  TriageRelationshipType.SPOUSE,
  TriageRelationshipType.BENEFICIARY,
  TriageRelationshipType.PERSTIRPES,
  TriageRelationshipType.EXECUTOR,
  TriageRelationshipType.TRUSTEE,
] as const;

/**
 * Get allowed relationship types for a given account type.
 * Returns them in display order for UI consistency.
 *
 * @param accountType - The triage account type
 * @param isMultiInheritanceEstateToggle - Whether the estate toggle is enabled
 * @param isInheritanceIndividualJointToTrustToggle - Whether the individual/joint to trust toggle is enabled
 * @returns Array of { key, value } pairs for allowed relationships
 */
export function getAllowedRelationshipTypes(
  accountType: TriageAccountType,
  isMultiInheritanceEstateToggle: boolean = false,
  isInheritanceIndividualJointToTrustToggle: boolean = false,
): Array<{ key: string; value: string }> {
  const config =
    ALLOWED_RELATIONSHIPS_CONFIG[accountType as keyof typeof ALLOWED_RELATIONSHIPS_CONFIG];

  if (!config) {
    return [];
  }

  // Determine which set to use based on toggles.
  // When multiple toggles are active, merge all applicable relationship sets.
  const relationshipSets: ReadonlySet<TriageRelationshipType>[] = [config.default];

  if ('withEstateToggle' in config && isMultiInheritanceEstateToggle) {
    relationshipSets.push(config.withEstateToggle);
  }

  if ('withIndividualJointToTrustToggle' in config && isInheritanceIndividualJointToTrustToggle) {
    relationshipSets.push(config.withIndividualJointToTrustToggle);
  }

  const allowedSet = new Set(relationshipSets.flatMap((relationshipSet) => [...relationshipSet]));

  // Filter and return in display order
  return RELATIONSHIP_DISPLAY_ORDER.filter((type) => allowedSet.has(type)).map((type) => ({
    key: type,
    value: triageRelationshipTypesMap.get(type) || '',
  }));
}
